import { useObservable } from "azure-devops-ui/Core/Observable";
import React from "react";
import { unstable_batchedUpdates } from "react-dom";
import { lazy } from "../../../common/components/lazy/lazy";
import { Observer } from "../../../common/components/observer/observer";
import { Scenario } from "../../../common/components/scenario/scenario";
import { EventContext } from "../../../common/contexts/event";
import { useFetch } from "../../../common/hooks/usefetch";
import { EventDispatch } from "../../../common/utilities/dispatch";
import { IFetchCompleteEvent, IFetchPrepareEvent } from "../../../common/utilities/fetch";
import { format } from "../../../common/utilities/format";
import { requestBadgerToken, requestRedeemToken, validatePassword } from "../../api/auth";
import { getApiConfiguration } from "../../api/util";
import { AuthorizationContext, PhotoAuthorizationContext } from "../../contexts/authorization";
import { SessionContext } from "../../contexts/session";
import { useRejection } from "../../hooks/userejection";

const PasswordValidationDialog = lazy(() => import("../../dialogs/passwordvalidationdialog"));

export interface ISecurityBoundaryProps {
  id: string;
  url: URL;
}

export function SecurityBoundary(props: ISecurityBoundaryProps & { children: React.ReactNode }): React.ReactElement | null {
  const { children, url, id } = props;

  const parentEventContext = React.useContext(EventContext);
  const parentSessionContext = React.useContext(SessionContext);

  const validationToken = React.useRef<string | undefined>();

  const [badgerToken, setBadgerToken] = React.useState(() => {
    const token = window.history.state?.state?.badgerToken;

    return token || undefined;
  });
  const [badgerPromise, setBadgerPromise] = React.useState<Promise<string> | undefined>();
  const [eventContext] = React.useState(() => new EventDispatch({ parentDispatch: parentEventContext }));
  const [migrated, setMigrated] = React.useState<boolean>(false);
  const [showPasswordDialog, setShowPasswordDialog] = useObservable(false);
  const [validated, setValidated] = React.useState(0);

  // Create an error handler for reporting api errors to the application.
  const handleRejection = useRejection();

  const authkey = url.searchParams.get("authkey") || undefined;
  const driveId = url.searchParams.get("cid") || undefined;

  // Network calls used to redeem share token and request badger token
  const _requestBadgerToken = useFetch(requestBadgerToken, { name: "requestBadgerToken" });
  const _requestRedeemToken = useFetch(requestRedeemToken, { blocking: false, eventDispatch: eventContext, name: "requestRedeemToken" });
  const _validatePassword = useFetch(validatePassword, { blocking: false, eventDispatch: eventContext, name: "validatePassword" });

  React.useEffect(() => {
    eventContext.addEventListener("fetchPrepare", prepareRequest);
    eventContext.addEventListener("fetchComplete", completeRequest);

    return () => {
      eventContext.removeEventListener("fetchPrepare", prepareRequest);
      eventContext.removeEventListener("fetchComplete", completeRequest);
    };
  });

  const childSessionContext = React.useMemo(() => {
    const context = { ...parentSessionContext };
    context.driveId = driveId ?? parentSessionContext.driveId;
    context.migrated = migrated;
    return context;
  }, [driveId, migrated, parentSessionContext]);

  const authorizationContext = React.useMemo(() => {
    if (parentSessionContext.authenticated()) {
      return new PhotoAuthorizationContext(eventContext, childSessionContext, badgerToken);
    } else {
      return new PhotoAuthorizationContext(eventContext, childSessionContext, authkey, badgerToken);
    }
  }, [eventContext, childSessionContext, parentSessionContext, authkey, badgerToken]);

  return (
    <EventContext.Provider value={eventContext}>
      <AuthorizationContext.Provider value={authorizationContext}>
        <SessionContext.Provider value={childSessionContext}>
          <React.Fragment key={validated}>{children}</React.Fragment>
        </SessionContext.Provider>
        <Observer values={{ showPasswordDialog }}>
          {({ showPasswordDialog }) =>
            showPasswordDialog ? (
              <React.Suspense fallback={null}>
                <Scenario scenarioName="validatePassword">
                  <PasswordValidationDialog
                    onDismiss={() => setShowPasswordDialog(false)}
                    validatePassword={(password) => {
                      if (badgerPromise) {
                        return badgerPromise.then((token) => {
                          validationToken.current = token;

                          // Perform the validation now that we have the badger token ready.
                          return _validatePassword(id, password, authkey || "", childSessionContext.authenticated()).then(() => {
                            unstable_batchedUpdates(() => {
                              setBadgerToken(token);
                              setValidated(validated + 1);
                              setShowPasswordDialog(false);
                            });
                          });
                        });
                      } else {
                        return _validatePassword(id, password, authkey || "", childSessionContext.authenticated()).then(() => {
                          unstable_batchedUpdates(() => {
                            setValidated(validated + 1);
                            setShowPasswordDialog(false);
                          });
                        });
                      }
                    }}
                  />
                </Scenario>
              </React.Suspense>
            ) : null
          }
        </Observer>
      </AuthorizationContext.Provider>
    </EventContext.Provider>
  );

  function prepareRequest(event: IFetchPrepareEvent) {
    const apiConfiguration = getApiConfiguration();

    // First prepare the origin and any potential apiVersion segments
    event.url = format(event.url, apiConfiguration.apiOrigin);
    event.url = format(event.url, apiConfiguration.apiVersion);

    // Lookup the configuration for the target domain.
    const url = new URL(event.url);
    const configuration = apiConfiguration.domainSettings[url.hostname];

    // If this domain supports the badger process either with authkey's or direct
    // badger tokens we will add any available to the requests.
    if (configuration.badger) {
      if (badgerToken || validationToken.current) {
        event.init.headers = Object.assign({ Authorization: `Badger ${badgerToken || validationToken.current}` }, event.init.headers);
      } else if (authkey && !url.searchParams.get("authkey")) {
        url.searchParams.set("authkey", authkey);
        event.url = url.toString();
      }
    }
  }

  function completeRequest(event: IFetchCompleteEvent) {
    if (event.result.status === "rejected") {
      if (event.result.reason?.error?.innererror?.code === "sharingPasswordRequired") {
        if (childSessionContext.authenticated()) {
          setShowPasswordDialog(true);
        } else {
          unstable_batchedUpdates(() => {
            setBadgerPromise(
              _requestBadgerToken()
                .then((token) => {
                  const redeem = url.searchParams.get("redeem") || undefined;

                  if (redeem) {
                    return _requestRedeemToken(redeem)
                      .then(() => {
                        if (driveId) {
                          setMigrated(true);
                        }

                        return token;
                      })
                      .catch((error) => {
                        handleRejection(error);

                        return error;
                      });
                  }

                  return token;
                })
                .catch((error) => {
                  handleRejection(error);

                  return error;
                })
            );

            setShowPasswordDialog(true);
          });
        }
      } else if (event.result.reason?.error?.innererror?.code === "sharingPasswordIncorrect") {
        const { SharingPasswordError } = window.Resources.PasswordDialog;

        eventContext.dispatchEvent("showMessage", {
          messageType: "error",
          timeout: 3000,
          title: () => SharingPasswordError
        });
      }
    }
  }
}
